import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Users, BadgeCheck, Activity, AlertTriangle, CalendarDays, Settings as SettingsIcon, Shield, Globe } from "lucide-react";

/**
 * Iter-41: dedicated dashboard for ADMIN role. Focuses strictly on user-management /
 * settings indicators (no missions, billing, JH, validations, AR aging — those are out
 * of admin's scope per iter-40).
 */
const KPI = ({ label, value, sub, to, testid }) => {
  const inner = (
    <div className="bg-white border border-zinc-200 rounded-lg p-5 hover:border-brand-navy/40 transition-colors">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">{label}</div>
      <div className="text-3xl font-heading font-bold text-zinc-900 leading-none">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-2">{sub}</div>}
    </div>
  );
  return to ? <Link to={to} data-testid={testid} className="block">{inner}</Link> : <div data-testid={testid}>{inner}</div>;
};

const Shortcut = ({ to, icon: Icon, label, description, testid }) => (
  <Link to={to} data-testid={testid} className="group flex items-start gap-3 bg-white border border-zinc-200 rounded-lg p-5 hover:border-brand-navy hover:shadow-sm transition-all">
    <div className="shrink-0 p-2.5 bg-brand-cream rounded-md text-brand-navy group-hover:bg-brand-orange-100 group-hover:text-brand-orange-600 transition-colors">
      <Icon size={18} strokeWidth={1.75} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-heading font-semibold text-zinc-900 text-[15px] leading-snug">{label}</div>
      <div className="text-xs text-zinc-500 mt-1">{description}</div>
    </div>
  </Link>
);

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [missingContract, setMissingContract] = useState(0);
  const [last24h, setLast24h] = useState(null);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
    api.get("/freelance/admin/missing-contract").then((r) => setMissingContract((r.data || []).length)).catch(() => {});
    api.get("/admin/last-24h-stats").then((r) => setLast24h(r.data)).catch(() => setLast24h(null));
  }, []);

  const total = users.length;
  const active = users.filter((u) => u.status === "ACTIVE").length;
  const pending = users.filter((u) => u.status === "PENDING").length;
  const suspended = users.filter((u) => u.status === "SUSPENDED").length;
  const ceoCount = users.filter((u) => u.role === "CEO").length;
  const managerCount = users.filter((u) => u.role === "MANAGER").length;
  const gestionnaireCount = users.filter((u) => u.role === "GESTIONNAIRE").length;
  const adminCount = users.filter((u) => u.role === "ADMIN").length;
  const collaboratorCount = users.filter((u) => u.role === "COLLABORATOR" && !u.is_freelance).length;
  const freelanceCount = users.filter((u) => u.is_freelance).length;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Iter-42: Last 24h pulse — account-management activity */}
      {last24h && (
        <div data-testid="admin-last-24h" className="bg-brand-navy text-white rounded-lg p-5">
          <div className="flex items-baseline justify-between gap-4 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-mono mb-1">Last 24h · Account activity</div>
              <div className="font-heading text-lg font-semibold">Admin pulse</div>
            </div>
            <Link to="/admin/users" className="text-[11px] text-white/70 hover:text-white underline-offset-2 hover:underline">Open Users &amp; roles →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Link to="/admin/users?last24h=approved" data-testid="last24h-approved" className="bg-white/10 hover:bg-white/20 transition-colors rounded-md p-3 group">
              <div className="text-[9px] uppercase tracking-widest text-white/60 font-mono mb-1">Accounts approved</div>
              <div className="text-2xl font-heading font-bold leading-none group-hover:underline underline-offset-2">{last24h.approved}</div>
            </Link>
            <Link to="/admin/users?last24h=suspended" data-testid="last24h-suspended" className="bg-white/10 hover:bg-white/20 transition-colors rounded-md p-3 group">
              <div className="text-[9px] uppercase tracking-widest text-white/60 font-mono mb-1">Suspensions</div>
              <div className="text-2xl font-heading font-bold leading-none group-hover:underline underline-offset-2">{last24h.suspended}</div>
            </Link>
            <Link to="/admin/users?last24h=unsuspended" data-testid="last24h-unsuspended" className="bg-white/10 hover:bg-white/20 transition-colors rounded-md p-3 group">
              <div className="text-[9px] uppercase tracking-widest text-white/60 font-mono mb-1">Reactivations</div>
              <div className="text-2xl font-heading font-bold leading-none group-hover:underline underline-offset-2">{last24h.unsuspended}</div>
            </Link>
            <Link to="/admin/users?last24h=role_changed" data-testid="last24h-role-changed" className="bg-white/10 hover:bg-white/20 transition-colors rounded-md p-3 group">
              <div className="text-[9px] uppercase tracking-widest text-white/60 font-mono mb-1">Role changes</div>
              <div className="text-2xl font-heading font-bold leading-none group-hover:underline underline-offset-2">{last24h.role_changed}</div>
            </Link>
            <Link to="/admin/users?last24h=deleted" data-testid="last24h-deleted" className="bg-white/10 hover:bg-white/20 transition-colors rounded-md p-3 group">
              <div className="text-[9px] uppercase tracking-widest text-white/60 font-mono mb-1">Deletions</div>
              <div className="text-2xl font-heading font-bold leading-none group-hover:underline underline-offset-2">{last24h.deleted}</div>
            </Link>
          </div>
        </div>
      )}

      {/* KPI row — strictly user-management indicators */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Scope · Accounts &amp; access</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI label="Active accounts" value={active} sub={`out of ${total} accounts`} to="/admin/users" testid="admin-kpi-active" />
          <KPI
            label="Pending approval"
            value={pending}
            sub={pending > 0 ? "Click to approve" : "No requests"}
            to={pending > 0 ? "/admin/users?tab=pending" : "/admin/users"}
            testid="admin-kpi-pending"
          />
          <KPI label="Suspended accounts" value={suspended} sub="See Users & roles" to="/admin/users" testid="admin-kpi-suspended" />
          <KPI label="Freelances" value={freelanceCount} sub="Read-only" to="/admin/users" testid="admin-kpi-freelance" />
        </div>
      </div>

      {/* Role breakdown */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Breakdown by role</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KPI label="CEO" value={ceoCount} testid="admin-role-ceo" />
          <KPI label="Managers" value={managerCount} testid="admin-role-manager" />
          <KPI label="Gestionnaires" value={gestionnaireCount} testid="admin-role-gestionnaire" />
          <KPI label="Admins" value={adminCount} testid="admin-role-admin" />
          <KPI label="Collaborateurs internes" value={collaboratorCount} testid="admin-role-collab" />
        </div>
      </div>

      {/* Quick links to admin scope */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Quick access</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Shortcut
            to="/admin/users"
            icon={Users}
            label="Users &amp; roles"
            description="Approve new requests, adjust eligible roles, suspend."
            testid="admin-shortcut-users"
          />
          <Shortcut
            to="/admin/directory"
            icon={BadgeCheck}
            label="Workspace directory"
            description="List of all synced Google Workspace accounts."
            testid="admin-shortcut-directory"
          />
          <Shortcut
            to="/admin/custom-roles"
            icon={Shield}
            label="Custom roles"
            description="Granular permissions for non-standard roles."
            testid="admin-shortcut-customroles"
          />
          <Shortcut
            to="/settings/alerts"
            icon={AlertTriangle}
            label="Alertes &amp; rapport"
            description="MD thresholds per collaborator / mission, monthly CEO report."
            testid="admin-shortcut-alerts"
          />
          <Shortcut
            to="/settings/leaves"
            icon={CalendarDays}
            label="Leaves &amp; public holidays"
            description="Annual quota, carry-over credit, public holiday calendar."
            testid="admin-shortcut-leaves"
          />
        </div>
      </div>

      {/* Iter-44: hidden from admin per spec — admin has no action rights on freelance profiles. */}
    </div>
  );
}
